import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  VideoLibrary as ContentIcon,
  Settings as SettingsIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { Header } from '../Header';
import { Sidebar } from '../Sidebar';
import { NavigationItem } from '../layout.types';

const drawerWidth = 240;

export const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);

  const navigationItems: NavigationItem[] = [
    {
      key: 'dashboard',
      path: '/dashboard',
      icon: React.createElement(DashboardIcon),
    },
    {
      key: 'content',
      path: '/content',
      icon: React.createElement(ContentIcon),
    },
    {
      key: 'settings',
      path: '/settings',
      icon: React.createElement(SettingsIcon),
    },
  ];

  const adminNavigationItems: NavigationItem[] = [
    {
      key: 'admin',
      path: '/admin',
      icon: React.createElement(SupervisorAccountIcon),
      requiredRoles: ['admin'],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isItemVisible = (item: NavigationItem) => {
    if (!item.requiredRoles || !user) return true;
    return item.requiredRoles.includes(user.role);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header
        drawerOpen={drawerOpen}
        onDesktopDrawerToggle={handleDesktopDrawerToggle}
        onMobileDrawerToggle={handleDrawerToggle}
        drawerWidth={drawerWidth}
      />

      <Sidebar
        mobileOpen={mobileOpen}
        drawerOpen={drawerOpen}
        onMobileDrawerToggle={handleDrawerToggle}
        navigationItems={navigationItems}
        adminNavigationItems={adminNavigationItems}
        drawerWidth={drawerWidth}
        onNavigate={handleNavigation}
        isItemVisible={isItemVisible}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { 
            xs: '100%',
            md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
          },
          maxWidth: '100%',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
